/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { useSelect } from "downshift";
import { useRef, ButtonHTMLAttributes, useState, useCallback, useMemo, KeyboardEvent, useEffect } from "react";
import { noop } from "../../../../constants/noop";
import { useTheme } from "../../../../theme/utils/useTheme";
import { InputFieldBaseProps, getInputFieldHeightValue } from "../../../../util/inputFieldBaseProps";
import { Chip } from "../../../chip";
import { ChipColorVariant } from "../../../chip/types";
import { useFocusMode } from "../../../focusMode";
import { FORM_LABEL_CLICK_EVENT_ID } from "../../../formlabel/FormLabel";
import { SELECT_TOGGLE_ELEMENT_CLASS } from "../../constants";
import { useA11yStatusMessages } from "../../hooks/useA11yStatusMessages";
import { useHorizontalResize } from "../../hooks/useHorizontalResize";
import { useSelectFlyout } from "../../hooks/useSelectFlyout";
import { BaseOptionType } from "../../types";
import { useSelectIds, isSelectV2OptionSeparator, findNextValidOptionIndex, getChipColor } from "../../utils";
import { SelectButton } from "../SelectButton";
import { SelectButtonText } from "../SelectButtonText";
import { selectBasicFontTheme, selectV2Theme } from "../selectV2Theme";
import { ActionButtonBaseCss } from "../shared/ActionButton";
import { Actions } from "../shared/Actions";
import { HiddenInput } from "../shared/HiddenInput";
import { Root } from "../shared/Root";
import { SelectClearButton } from "../shared/SelectClearButton";
import { SelectMenu } from "../shared/SelectMenu";
import { SelectPlaceholder } from "../shared/SelectPlaceholder";
import { ToggleButtonIcon } from "../shared/ToggleButtonIcon";
import { ContentBeforeContainer } from "./ContentBeforeContainer";
import { OptionalPropsNowRequired } from "./types";

interface FakeActionButtonProps extends InputFieldBaseProps {
    themeMode: RequiredThemeKeys;
    isOpen?: boolean;
}

// Necessary due to the computeRightPadding in SelectButton.tsx
const ActionsStyled = styled(Actions)`
    position: absolute;
    right: 0;
`;
// The whole select is the toggle button here, so we add a toggle-button-looking thing for
// consistency. Users love consistency and they LOVE chevrons that point down.
// If action button is used, an axe error is thrown because toggle would have a focusable
// element inside it
const FakeActionButton = styled("span")<FakeActionButtonProps>`
    ${({ themeMode, inputFieldHeight }) => ActionButtonBaseCss({ themeMode, inputFieldHeight })}
    ${({ isOpen, themeMode }) =>
        isOpen
            ? `
                background-color:  ${selectV2Theme.actionButtons.background.active(themeMode)};
            `
            : ``}
    &:hover {
        background-color: ${({ themeMode }) => selectV2Theme.actionButtons.background.hover(themeMode)};
    }
    &:active {
        background-color: ${({ themeMode }) => selectV2Theme.actionButtons.background.active(themeMode)};
    }
`;

interface SelectPropsBase<Option extends BaseOptionType>
    extends InputFieldBaseProps,
        Omit<OptionalPropsNowRequired<Option, HTMLButtonElement>, "isSearchable" | "onCreateOption"> {
    onChange: (value: Option | null) => void;
    contentBefore?: React.ReactNode;
}

export const SelectBasic = <Option extends BaseOptionType>({
    appearance = "outlined",
    "aria-describedby": ariaDescribedby,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-required": ariaRequired,
    classes,
    className,
    clientId,
    contentBefore,
    formatOption,
    getHiddenInputValue,
    hiddenInputName,
    id,
    innerRef,
    isLoading,
    isOptionDisabled,
    isReadonly,
    isClearable = false,
    menuWidth = "match-control",
    maxMenuHeight,
    maxOptionsUntilVirtualization,
    menuPlacement = "bottom",
    menuPosition,
    menuId,
    onBlur = noop,
    onChange,
    onFocus = noop,
    optionHeight,
    options,
    optionToString,
    portalProps,
    selectedOption,
    getOptionKey,
    validationState,
    menuIsOpen,
    setMenuIsOpen,
    onMenuIsOpenChanged,
    hideDropdownArrow = false,
    formatSelectedValue,
    placeholder,
    onKeyDown: providedOnKeyDown,
    inputFieldHeight,
    renderChipColor,
    overrides,
}: SelectPropsBase<Option>) => {
    const a11yMessages = useA11yStatusMessages();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { focusMode } = useFocusMode();

    const isInteractive = !isReadonly;
    const [isFocused, setIsFocused] = useState(false);
    const changeType = useRef<string | null>(null);

    // The flyout needs to point to the component container, but otherwise flyout interactions
    // should affect the input.
    const { ref: resizeRef, width: targetWidth } = useHorizontalResize();
    const localToggleRef = useRef<HTMLButtonElement>(null);
    const toggleButtonRef = useForkRef(localToggleRef, resizeRef, innerRef || null);
    const selectIds = useSelectIds("select", id);

    const { closeMenu, getItemProps, getMenuProps, getToggleButtonProps, highlightedIndex, isOpen, openMenu } =
        useSelect({
            ...a11yMessages,
            ...selectIds,
            ...(menuIsOpen !== undefined ? { isOpen: menuIsOpen } : null),
            selectedItem: null,
            items: options,
            circularNavigation: false,
            itemToString: optionToString,
            stateReducer(state, { changes, type }) {
                changeType.current = type;
                switch (type) {
                    case useSelect.stateChangeTypes.MenuKeyDownEscape:
                        // Cancel the escape key handling. We want to handle this key on our own
                        // through the StackOrderManager.
                        return state;
                    case useSelect.stateChangeTypes.MenuKeyDownEnter:
                    case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                    case useSelect.stateChangeTypes.ItemClick:
                        if (changes.selectedItem) {
                            return isOptionDisabled?.(changes.selectedItem) ? state : changes;
                        }
                }

                if (changes.highlightedIndex) {
                    switch (type) {
                        case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
                        case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
                            if (isSelectV2OptionSeparator(options[changes.highlightedIndex])) {
                                return {
                                    ...changes,
                                    highlightedIndex: findNextValidOptionIndex(
                                        options,
                                        changes.highlightedIndex,
                                        type === useSelect.stateChangeTypes.MenuKeyDownArrowDown
                                    ),
                                };
                            }
                    }
                }

                return changes;
            },
            onStateChange: ({ type, selectedItem }) => {
                switch (type) {
                    case useSelect.stateChangeTypes.MenuKeyDownEscape:
                    case useSelect.stateChangeTypes.MenuKeyDownEnter:
                    case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
                    case useSelect.stateChangeTypes.ItemClick:
                        if (selectedItem) {
                            // TODO are there cases here where the selectedItem is null?
                            onChange(selectedItem);
                        }
                        break;
                }
            },
            onIsOpenChange: ({ isOpen: openState }) => {
                if (process.env.NODE_ENV !== "production") {
                    warnOnceForDeprecatedSetMenuIsOpenProp =
                        warnOnceForDeprecatedSetMenuIsOpenProp ??
                        warnOnce(
                            "SelectV2 (basic): setMenuIsOpen is deprecated. Please use onMenuIsOpenChanged instead."
                        );

                    if (setMenuIsOpen) {
                        warnOnceForDeprecatedSetMenuIsOpenProp();
                    }
                }
                // setMenuIsOpen is deprecated. Use onMenuIsOpenChanged if it's available instead.
                (onMenuIsOpenChanged ?? setMenuIsOpen)?.(!!openState);
            },
        });

    const { flyoutProps, targetRef } = useSelectFlyout<HTMLButtonElement>({
        menuId,
        clientId,
        closeMenu,
        isOpen,
        targetRef: toggleButtonRef,
        menuPlacement,
        menuPosition,
        portalProps,
        updateKey: String(isOpen),
    });

    const toggleButtonProps = getToggleButtonProps({
        // This will remove some mouse and key handling.
        // Unlike useCombobox, we don't need to handle chip navigation which these handlers
        // assist with.
        disabled: !isInteractive,
        ref: targetRef,
        onKeyDown: (e) => {
            if (providedOnKeyDown) {
                providedOnKeyDown(e, {
                    highlightedIndex,
                    options,
                });
            }
        },
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledby,
        "aria-describedby": ariaDescribedby,
        "aria-required": ariaRequired,
        id,
    }) as ButtonHTMLAttributes<HTMLButtonElement>;

    // We don't ever want to include this; the button must still be focusable.
    delete toggleButtonProps["disabled"];

    const handleSelectBasicFocus = (event: React.FocusEvent<HTMLDivElement>, type: "blur" | "focus") => {
        //eliminate cases where the button is the target
        //this way we are only firing the onFocus/onBlur events when the focus is on the container
        if (event.target.tagName !== "BUTTON") {
            if (type === "focus") {
                setIsFocused(true);
                onFocus?.(event as unknown as React.FocusEvent<HTMLButtonElement>);
            } else {
                setIsFocused(false);
                onBlur?.(event as unknown as React.FocusEvent<HTMLButtonElement>);
            }
        } else {
            //manage focus for styles only without firing off onBlur or onFocus events
            if (type === "focus") {
                setIsFocused(true);
            } else {
                setIsFocused(false);
            }
        }
    };

    const inputHeight = getInputFieldHeightValue(themeMode, inputFieldHeight);

    const onMenuKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            providedOnKeyDown?.(e, {
                highlightedIndex,
                options,
            });
            switch (e.key) {
                case "Tab":
                    e.preventDefault();
                    localToggleRef.current?.focus();
                    break;
            }
        },
        [highlightedIndex, options, providedOnKeyDown]
    );
    const extraMenuProps = useMemo(() => {
        return {
            onKeyDown: onMenuKeyDown,
            ...overrides?.menuProps,
        };
    }, [onMenuKeyDown, overrides?.menuProps]);
    // crazy optimization, shouldn't SelectMenu just accept an optional selectedItems?
    const EMPTY_SELECTED_ITEMS: Option[] = useMemo(() => [], []);

    useEffect(() => {
        if (!isOpen) {
            //wait a tick before resetting the changeType
            //we do this so label clicks can distinguish between
            //input blurs that happen when it's clicked vs
            //blurs from other element clicks.
            setTimeout(() => (changeType.current = null), 0);
        }
    }, [isOpen]);

    return (
        <Root
            inputFieldHeight={inputFieldHeight}
            data-client-id={getClientId(clientId, "-root")}
            className={className}
            isFocused={isFocused || isOpen}
            validationState={validationState || "rest"}
            isReadonly={isReadonly}
            focusMode={focusMode}
            themeMode={themeMode}
            appearance={appearance}
            onBlur={(e: React.FocusEvent<HTMLDivElement>) => handleSelectBasicFocus(e, "blur")}
            onFocus={(e: React.FocusEvent<HTMLDivElement>) => handleSelectBasicFocus(e, "focus")}
            {...overrides?.rootProps}
        >
            <HiddenInput
                clientId={clientId}
                selectionType={selectedOption}
                getHiddenInputValue={getHiddenInputValue}
                hiddenInputName={hiddenInputName}
            />
            <SelectButton
                inputFieldHeight={inputFieldHeight}
                type="button"
                data-menu-test-id={menuId}
                clientId={getClientId(clientId, "-toggle")}
                // A shared class for selects that the FormLabel can target when it dispatches a click event.
                className={SELECT_TOGGLE_ELEMENT_CLASS}
                hideDropdownArrow={hideDropdownArrow}
                isClearable={isClearable}
                hasContentBefore={!!contentBefore}
                themeMode={themeMode}
                // We'll reattach our own rather than relying on Clickable's ring.
                omitFocusRing
                {...toggleButtonProps}
                onClick={(e) => {
                    // we dispatch a click event with a distinct detail key if it's a label click
                    // this way we can functionally open the menu rather than
                    // relying on the blur and toggle click that causes the menu to close
                    // and quickly open again
                    if (e.detail === FORM_LABEL_CLICK_EVENT_ID) {
                        if (!isOpen && changeType.current !== useSelect.stateChangeTypes.MenuBlur) {
                            openMenu();
                        } else {
                            closeMenu();
                        }
                    } else {
                        toggleButtonProps.onClick?.(e);
                    }
                }}
            >
                {contentBefore ? (
                    <ContentBeforeContainer themeMode={themeMode}>{contentBefore}</ContentBeforeContainer>
                ) : null}
                {formatSelectedValue !== undefined ? (
                    formatSelectedValue({ option: selectedOption })
                ) : (
                    <SelectButtonText
                        // @ts-expect-error variant type is complaining
                        variant={selectBasicFontTheme.variant(themeMode)}
                        themeMode={themeMode}
                        lineClamp={1}
                    >
                        {/* when a color value is present and renderChipColor is passed to avoid accidental chip coloring - display selected option as a colored Chip */}
                        {renderChipColor && selectedOption?.color ? (
                            <Chip
                                size={selectV2Theme.chip.size[inputHeight](themeMode)}
                                color={getChipColor(selectedOption.color as ChipColorVariant)}
                            >
                                {optionToString(selectedOption) || ""}
                            </Chip>
                        ) : (
                            optionToString(selectedOption as Option) || ""
                        )}
                    </SelectButtonText>
                )}

                {selectedOption === null && placeholder && (
                    <SelectPlaceholder inputFieldHeight={inputFieldHeight} hasContentBefore={!!contentBefore}>
                        {placeholder}
                    </SelectPlaceholder>
                )}
            </SelectButton>
            {isInteractive && (
                <ActionsStyled
                    // This will not be rendered if the control is not interactive.
                    inputFieldHeight={inputFieldHeight}
                    isReadonly={false}
                    themeMode={themeMode}
                >
                    {!hideDropdownArrow && (
                        <FakeActionButton
                            inputFieldHeight={inputFieldHeight}
                            isOpen={isOpen}
                            themeMode={themeMode}
                            onMouseDown={(e) => {
                                if (e.detail >= 2) {
                                    // If this is a doubleclick's mousedown, prevent text
                                    // highlighting. This is only an issue because we're pretending
                                    // this span is a button. Otherwise, the button would block
                                    // text selection on double click.
                                    e.preventDefault();
                                }
                            }}
                            onClick={() => {
                                if (!isOpen) {
                                    openMenu();
                                } else {
                                    closeMenu();
                                }
                            }}
                            onMouseUp={(event) => {
                                if (isOpen) {
                                    // If we don't stop propagation here, downshift will see this
                                    // mouseup as a click outside the component since it's floating
                                    // outside the toggle button. Downshift will then attempt to close
                                    // the menu. Since we want to call openMenu/closeMenu in onClick
                                    // ourselves, we need to tell downshift to chill.
                                    event.stopPropagation();
                                }
                            }}
                        >
                            <ToggleButtonIcon />
                        </FakeActionButton>
                    )}
                    {isClearable && selectedOption && (
                        <SelectClearButton
                            inputFieldHeight={inputFieldHeight}
                            clearSelection={onChange}
                            isMultiSelect={false}
                            resetFiltering={() => {}}
                            refocusTargetElement={() => {
                                localToggleRef.current?.focus();
                            }}
                        />
                    )}
                </ActionsStyled>
            )}
            <SelectMenu<Option>
                allSelected={false}
                enableSelectAll={false}
                extraMenuProps={extraMenuProps}
                flyoutProps={flyoutProps}
                formatOption={formatOption}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                className={classes?.menu}
                highlightedIndex={highlightedIndex}
                inputValue={""}
                isLoading={isLoading}
                isOpen={isOpen}
                isOptionDisabled={isOptionDisabled}
                menuWidth={menuWidth}
                maxMenuHeight={maxMenuHeight}
                maxOptionsUntilVirtualization={maxOptionsUntilVirtualization}
                optionHeight={optionHeight}
                options={options}
                optionToString={optionToString}
                selectedItems={EMPTY_SELECTED_ITEMS}
                selectedOption={selectedOption}
                targetWidth={targetWidth}
                getOptionKey={getOptionKey}
                additionalSelectFlyoutProps={overrides?.selectFlyoutProps}
            />
        </Root>
    );
};

let warnOnceForDeprecatedSetMenuIsOpenProp: undefined | (() => void);
